import {
    EmploymentStatus,
    PayType,
    PayPer,
    IncomeTypes,
    EmploymentStatusOther,
    Residency
} from '@deliveriesondemand/dod-data';
import {
    formatAddress,
    formatDuration,
    formatPhone,
    getIncome,
    sumTotal
} from '@deliveriesondemand/dod-utils';
import {connect} from 'react-redux';
import {Component, Fragment} from 'react';
import {formatMoney, formatNumber} from 'accounting';
import moment from 'moment';
import classNames from 'classnames';
import injectSheet from 'react-jss';

import {AppSteps} from '-/components/general/AppData';
import BuyerInfoService from '-/services/BuyerInfoService';
import DialogService from '-/services/DialogService';
import VehicleInfoService from '-/services/VehicleInfoService';
import EmploymentInfoService from '-/services/EmploymentInfoService';
import AdditionalIncomeService from '-/services/AdditionalIncomeService';
import SessionService from '-/services/SessionService';
import UIService from '-/services/UIService';
import NavigationService from '-/services/NavigationService';
import ApiService from '-/services/ApiService';
import CreditorService from '-/services/CreditorService';
import DealInfoService from '-/services/DealInfoService';
import Form from '-/components/fields/Form';
import Legend from '-/components/fields/Legend';
import Fieldset from '-/components/fields/Fieldset';
import Button from '-/components/general/Button';
import LowIncomeWarning from '-/components/general/LowIncomeWarning';
import {bounce, center, rem, size, userSelect, wordWrap} from '-/styles/utils';
import {get} from '-/util/lodash';
import ResidencyWarning from '-/components/general/ResidencyWarning';

const styles = theme => ({
    description: {
        paddingTop: rem(30),
        marginTop: 50
    },
    icon: {
        display: 'table',
        position: 'relative',
        margin: '0 auto'
    },
    text: {
        margin: rem(25)
    },
    check: {
        ...size('50px'),
        backgroundColor: theme.colors.secondary,
        right: rem(-24),
        position: 'absolute',
        bottom: rem(0),
        borderRadius: '50%',
        '&::before, &:before': {
            ...center('both'),
            content: '"\\f00c"',
            display: 'block',
            fontFamily: 'FontAwesome',
            fontSize: '150%',
            fontWeight: 'normal',
            lineHeight: rem(24),
            color: 'white'
        }
    },
    incomplete: {
        opacity: .2
    },
    success: {
        ...bounce()
    },
    h5: {
        fontSize: rem(18),
        fontWeight: 'normal',
        lineHeight: rem(19),
        color: theme.colors.strong,
        opacity: .9,
        marginBottom: rem(15),
        textAlign: 'center'
    },
    h3: {
        color: theme.colors.secondary,
        fontSize: rem(36),
        fontWeight: 'normal',
        lineHeight: rem(50),
        textAlign: 'center'
    },
    row: {
        display: 'flex',
        width: '100%'
    },
    cell: {
        verticalAlign: 'top',
        '&:first-child': {
            flex: 1
        }
    },
    staticwrap: {
        marginBottom: rem(15)
    },
    errorText: {
        color: 'red',
        fontSize: rem(18),
        marginBottom: rem(20)
    }
});

const controlStyles = theme => ({
    root: {
        marginBottom: rem(15),
        '&:last-child': {
            marginBottom: 0,
            '& $value': {
                '& p': {
                    marginBottom: 0
                }
            }
        }
    },
    label: {
        fontFamily: theme.fontFamily,
        fontSize: rem(18),
        fontWeight: 'bold',
        color: '#454545'
    },
    value: {
        fontSize: rem(18),
        '& p': {
            marginTop: rem(10),
            marginBottom: rem(20),
            fontFamily: theme.fontFamily,
            fontWeight: 'normal',
            color: '#717171',
            ...wordWrap()
        },
        '& ul': {
            listStyle: 'none',
            '& li': {
                marginTop: rem(10),
                fontFamily: theme.fontFamily,
                fontWeight: 'normal',
                color: '#717171',
                'input[type=\'radio\']': {
                    marginRight: 5
                }
            }
        }
    }
});

const editButtonStyles = theme => ({
    root: {
        color: theme.colors.secondary,
        ...size('40px'),
        ...userSelect('none'),
        border: 'none',
        fontSize: rem(24),
        lineHeight: rem(24),
        backgroundColor: 'transparent',
        cursor: 'pointer',
        outline: 0,
        transition: 'color 0.3s ease-in-out',
        WebkitTapHighlightColor: 'rgba(0, 0, 0, 0)',
        WebkitBackfaceVisibility: 'hidden'
    },
    edit: {
        fontSize: rem(18),
        fontWeight: 'normal',
        fontFamily: theme.fontFamily
    }
});

const SimpleStaticCls = props => {
    const {label, value, isList, classes = {}, showUnset = false} = props;
    if (!value && !showUnset) return null;
    return (
        <div className={classes.root}>
            <div className={classes.label}><b>{label}</b></div>
            <div className={classes.value}>{isList ? value : <p>{value}</p>}</div>
        </div>
    );
};

const SimpleStatic = injectSheet(controlStyles)(SimpleStaticCls);

const EditButtonImplCls = props => {
    const {onEditClick, step, status, isCoApplicant, classes = {}} = props;

    if (status === 'SUBMITTED') {
        return null;
    }
    return (
        <button className={classes.root} onClick={() => {onEditClick(step, isCoApplicant);}}>
            <i className="fa fa-pencil-square-o"><div className={classes.edit}>Edit</div></i>
        </button>
    );
};

const EditButtonImpl = injectSheet(editButtonStyles)(EditButtonImplCls);

const EditButton = connect(() => {
    return {
        status: UIService.getStatus()
    };
}, dispatch => {
    return {
        onEditClick: (step, isCoApplicant) => {
            dispatch(() => {
                NavigationService.toggleCoApplicant(isCoApplicant);
                NavigationService.next(step);
                ApiService.save('none');
            });
        }
    };

})(EditButtonImpl);

const getTimeAtCurrentAddress = (yearsAtAddress, monthsAtAddress) => {
    yearsAtAddress = yearsAtAddress || 0;
    let time = `${yearsAtAddress} Years`;
    if (monthsAtAddress) {
        time += `, ${monthsAtAddress} Months`;
    }
    return time;
};

class FinalReview extends Component {
    constructor(props) {
        super(props);

        this.state = {
            needsCoApp: false
        };
    }

    componentDidMount() {
        const {props} = this;
        const status = get(props, 'offer.employmentInfo.status');
        const modalStatuses = ['student', 'homeMaker', 'notWorking', 'other'];
        const additionalIncomeEntries = get(props, 'offer.additionalIncome.entries', []);
        const coAppUnlocked = get(props, 'offer.navigation.coAppUnlocked');
        const shouldShow = modalStatuses.includes(status) && !additionalIncomeEntries.length && !coAppUnlocked;

        if (shouldShow) {
            this.setState({
                needsCoApp: true
            });

            DialogService.show('CoAppReminder', {});
        }
    }

    getYearsAtCurrentAddress(years, months) {
        years = parseInt(years) || 0;
        months = parseInt(months) || 0;
        return years + (months / 12);
    }

    renderBuyerInfo(isCoApplicant) {
        const {classes = {}, contactPhone, isCashDeal, isPriorMemberAnswerRequired, offer, requireCitizenshipProof} = this.props;
        const {buyerInfo, buyerInfo_CoApp} = offer;
        const buyer = isCoApplicant ? buyerInfo_CoApp : buyerInfo;
        const {
            name,
            address,
            cellphone,
            email,
            priorMembershipStatus: priorMembershipStatusRaw,
            yearsAtAddress,
            monthsAtAddress,
            priorAddresses = [],
            dob,
            lastFour,
            residencyStatus: {
                residency
            } = {},
            preferredLanguage,
            ssn,
            ssnComplete
        } = buyer;

        const showSSNWarning = !lastFour && !ssn && !ssnComplete && !isCashDeal;
        const applicantType = isCoApplicant ? 'Co-Applicant ' : '';
        const applicantTitle = isCoApplicant ? 'Co-Applicant Information' : 'Applicant Information';
        const timeAtAddress = this.getYearsAtCurrentAddress(yearsAtAddress, monthsAtAddress);
        const priorMembershipStatus = priorMembershipStatusRaw === 'MEMBER' ? 'Yes' : 'No';
        const isResident = residency !== 'nonPermanentResident';
        let fields = [];
        let i = 0;

        if (timeAtAddress < CreditorService.getYearsResidencyHistoryRequired() && priorAddresses.length > 0) {
            const addresses = priorAddresses.filter(address => !!address.priorAddress && (address.yearsAtPriorAddress || address.monthsAtPriorAddress));
            if (addresses.length) {
                const priorAddressFields = priorAddresses.map((item, j) => {
                    const {priorAddress, monthsAtPriorAddress, yearsAtPriorAddress} = item;
                    return <li key={j}>{`${formatAddress(priorAddress)}, ${formatDuration(yearsAtPriorAddress, monthsAtPriorAddress)}`}</li>;
                });
                fields.push(<SimpleStatic isList={true} key={i++} value={<ul>{priorAddressFields}</ul>} label="Prior Addresses" />);
            }
        }

        return (
            <Fieldset type="flow">
                <Legend type="flow">{applicantTitle}</Legend>
                <div className={classes.row}>
                    <div className={classes.cell}>
                        <SimpleStatic label="Name" value={name} />
                        {dob && isCoApplicant && <SimpleStatic label="DOB" value={dob} />}
                        {showSSNWarning && (
                            <Fragment>
                                <SimpleStatic label="SSN" showUnset={true} />
                                <h4 className={classes.errorText}>
                                    Cannot submit credit application without SSN. Click &ldquo;Edit&ldquo;
                                    to add {applicantType}SSN.
                                </h4>
                            </Fragment>
                        )}
                        {requireCitizenshipProof && !isCashDeal &&
                            <SimpleStatic label="Residency" value={Residency[residency]} />
                        }
                        {!isResident && !isCashDeal && 
                            <ResidencyWarning contactPhone={contactPhone} />
                        }
                        {preferredLanguage &&
                            <SimpleStatic label="Preferred Language" value={preferredLanguage} />
                        }
                        <SimpleStatic label="Address" value={formatAddress(address)} />
                        {!isCashDeal &&
                            <SimpleStatic label="Time At Address" value={getTimeAtCurrentAddress(yearsAtAddress, monthsAtAddress)} />
                        }
                        {cellphone && (
                            <SimpleStatic label="Cell Phone" value={formatPhone(cellphone)} />
                        )}
                        <SimpleStatic label="Email" value={email} />
                        {!isCashDeal &&
                            <Fragment>
                                {isPriorMemberAnswerRequired &&
                                    <SimpleStatic label="Is Prior Member?" value={priorMembershipStatus} />
                                }
                                {fields}
                            </Fragment>
                        }
                    </div>
                    <div className={classes.cell}>
                        <EditButton step="buyerInfo" isCoApplicant={isCoApplicant} />
                    </div>
                </div>
            </Fieldset>
        );
    }

    renderCarInfo() {
        const {isCU, offer, classes = {}} = this.props;
        const {vehicleInfo} = offer;
        const {vin, year, make, model, styleDesc, mileage, mileageExact, bookout} = vehicleInfo || {};
        const {accessories: accessoriesRaw = []} = bookout || {};

        let mileageDisplay = '';

        if (isCU && mileage) {
            mileageDisplay = formatNumber(mileage) + ` (${mileageExact ? 'exact' : 'estimated'})`;
        }

        const accessories = accessoriesRaw.map((item, j) => {
            const {desc} = item;
            return <li key={j}>{desc}</li>;
        });

        return (
            <Fieldset type="flow">
                <Legend type="flow">{AppSteps.vehicleInfo.title}</Legend>
                <div className={classes.row}>
                    <div className={classes.cell}>
                        {vin && <SimpleStatic label="VIN" value={vin} />}
                        <SimpleStatic label="Year" value={year} />
                        <SimpleStatic label="Make" value={make} />
                        <SimpleStatic label="Model" value={model} />
                        <SimpleStatic label="Style" value={styleDesc} />
                        <SimpleStatic label="Mileage" value={mileageDisplay} />
                        {accessories && accessories.length !== 0 && <SimpleStatic isList={true} value={<ul>{accessories}</ul>} label="Accessories" />}
                    </div>
                    <div className={classes.cell}>
                        <EditButton step="vehicleInfo" isCoApplicant={false} />
                    </div>
                </div>
            </Fieldset>
        );
    }

    renderEmploymentInfo(isCoApplicant) {
        const {classes = {}, offer} = this.props;
        const {employmentInfo, employmentInfo_CoApp} = offer;
        const employment = isCoApplicant ? employmentInfo_CoApp : employmentInfo;
        const income = getIncome(offer);
        const applicantIncome = get(income, isCoApplicant ? 'coApp' : 'app');
        const {totalGrossMonthly} = applicantIncome;
        const {status, employerName, position, pay} = employment;
        const yearsRetired = get(employment, 'retirement.duration.years', 0);
        const monthsRetired = get(employment, 'retirement.duration.months', 0);

        let i = 0;
        let fields = [];
        if (status === 'employed') {
            fields.push(<SimpleStatic key={i++} value={employerName} label="Employer Name" />);
            fields.push(<SimpleStatic key={i++} value={position} label="Position/Title" />);
        }

        if (status === 'activeMilitary') {
            fields.push(<SimpleStatic key={i++} value={position} label="Position/Title" />);
            fields.push(<SimpleStatic key={i++} value={employmentInfo.payGrade} label="Pay Grade" />);
            const dutyTransferLabel = 'Is duty station transfer expected during the next year?';
            fields.push(<SimpleStatic key={i++} value={employmentInfo.dutyTransferExpected ? 'Yes' : 'No'} label={dutyTransferLabel} />);
            fields.push(<SimpleStatic key={i++} value={employmentInfo.dutyTransferWhere} label="Where are you being transfered to?" />);
            fields.push(<SimpleStatic key={i++} value={moment(employmentInfo.dutyTransferWhen).format('MMM Do YYYY')} label="When will the transfer occur?" />);
        }

        if (status === 'selfEmployed') {
            fields.push(<SimpleStatic key={i++} label="Type of Business" value={employmentInfo.typeOfBusiness} />);
            fields.push(<SimpleStatic key={i++} value={employerName} label="Company Name" />);
        }

        if (status === 'other') {
            fields.push(<SimpleStatic key={i++} label="More Details" value={EmploymentStatusOther[employmentInfo.statusOther]} />);
        }

        if (status === 'retired') {
            const {additionalIncome, additionalIncome_CoApp} = offer;
            const addlIncome = isCoApplicant ? additionalIncome_CoApp : additionalIncome;
            const {entries = []} = addlIncome;
            const nonZeroEntries = entries.filter(entry => {
                const {amount, type} = entry;

                return amount && ['retirementSocialSecurity', 'retirementPension'].includes(type);
            });
            const total = sumTotal(nonZeroEntries);

            fields.push(<SimpleStatic key={i++} value={yearsRetired} label="Years Retired" />);
            fields.push(<SimpleStatic key={i++} value={monthsRetired} label="Months Retired" />);

            if (nonZeroEntries.length) {
                fields.push(
                    <div className={classes.row} key={i++}>
                        <div className={classes.cell}>
                            {nonZeroEntries.map((income, idx) => {
                                const {amount, description, type} = income;

                                if (amount) {
                                    return (
                                        <div key={idx} className={classes.staticwrap}>
                                            <SimpleStatic label={IncomeTypes[type]} value={formatMoney(amount)} />
                                            {description && (
                                                <SimpleStatic label="Description" value={description} />
                                            )}
                                        </div>
                                    );
                                } else {
                                    return null;
                                }
                            })}
                            {total !== 0 && nonZeroEntries.length > 1 ? <SimpleStatic label="Total Monthly Retirement Income" value={formatMoney(total)} /> : null}
                        </div>
                    </div>
                );
            }
        }

        const {rate} = pay;
        const payRate = formatMoney(rate);

        if (status !== 'retired' && status !== 'other') {
            fields.push(<SimpleStatic key={i++} label="Pay Type" value={PayType[pay.payType]} />);

            if (pay.payType === 'hourly') {
                fields.push(<SimpleStatic key={i++} label="Hours per week" value={pay.hoursPerWeek} />);
                fields.push(<SimpleStatic key={i++} label="Pay Rate" value={`${payRate} per hour`} />);
            } else {
                fields.push(<SimpleStatic key={i++} label="Pay Rate" value={`${payRate} ${PayPer[pay.per]}`} />);
            }
        }

        fields.push(<SimpleStatic key={i++} label="Gross Monthly Income" value={formatMoney(totalGrossMonthly)} />);

        return (
            <Fieldset type="flow">
                <Legend type="flow">{AppSteps.employmentInfo.title + (isCoApplicant ? ' (Co-Applicant)' : '')}</Legend>
                <div className={classes.row}>
                    <div className={classes.cell}>
                        <SimpleStatic label="Employment Status" value={EmploymentStatus[status]} />
                        {fields}
                    </div>
                    <div className={classes.cell}>
                        <EditButton step="employmentInfo" isCoApplicant={isCoApplicant} />
                    </div>
                </div>
            </Fieldset>
        );
    }

    renderAdditionalInfo(isCoApplicant) {
        const {classes = {}, offer} = this.props;
        const {additionalIncome, additionalIncome_CoApp, employmentInfo, employmentInfo_CoApp} = offer;
        const income = isCoApplicant ? additionalIncome_CoApp : additionalIncome;
        const employment = isCoApplicant ? employmentInfo_CoApp : employmentInfo;
        const {status} = employment;
        const {entries = []} = income;
        const nonZeroEntries = entries.filter(entry => {
            const {amount, type} = entry;

            if (status === 'retired') {
                return !['retirementSocialSecurity', 'retirementPension'].includes(type);
            } else {
                return !!amount;
            }
        });
        const total = sumTotal(nonZeroEntries);

        if (nonZeroEntries.length <= 0) {
            return null;
        }

        return (
            <Fieldset type="flow">
                <Legend type="flow">{'Additional Income' + (isCoApplicant ? ' (Co-Applicant)' : '')}</Legend>
                <div className={classes.row}>
                    <div className={classes.cell}>
                        {nonZeroEntries.map((income, idx) => {
                            const {amount, description, type} = income;
                            if (amount) {
                                return (
                                    <div key={idx} className={classes.staticwrap}>
                                        <SimpleStatic label={IncomeTypes[type]} value={formatMoney(amount)} />
                                        {description && (
                                            <SimpleStatic label="Description" value={description} />
                                        )}
                                    </div>
                                );
                            } else {
                                return null;
                            }
                        })}
                        {total !== 0 && nonZeroEntries.length > 1 ? <SimpleStatic label="Total Additional Monthly Income" value={formatMoney(total)} /> : null}
                    </div>
                    <div className={classes.cell}>
                        <EditButton step="additionalIncome" isCoApplicant={isCoApplicant} />
                    </div>
                </div>
            </Fieldset>
        );
    }

    handleLowIncome = () => {
        this.setState({
            needsCoApp: true
        });
    };

    render() {
        const {needsCoApp} = this.state;
        const {isCashDeal, isCoApplicant, isDMSActive, status, classes = {}, contactPhone, offer, isCCRMode} = this.props;
        const coAppUnlocked = get(offer, 'navigation.coAppUnlocked', false);
        const buyerHasCoApp = get(offer, 'navigation.buyerHasCoApp');
        const iconCls = classNames(classes.check, classes.incomplete);
        const appResidency = get(offer, 'buyerInfo.residencyStatus.residency');
        const coAppResidency = get(offer, 'buyerInfo_CoApp.residencyStatus.residency');
        // don't allow to proceed if app or coapp are non-residents
        const isNonResident = appResidency === 'nonPermanentResident' || coAppResidency === 'nonPermanentResident';
        const hasAnsweredCoApp = buyerHasCoApp !== 'UNANSWERED';
        const hasCoApp = ['YES', 'YESPREVIOUSCOAPP'].includes(buyerHasCoApp);

        let coAppComp = 'submit';
        if (!isCoApplicant) {
            if (!hasAnsweredCoApp) {
                coAppComp = 'finalQuestions';
            } else if (hasCoApp) {
                coAppComp = 'addCoApp';
            }
        }

        return (
            <div>
                <section className={classes.description}>
                    {status !== 'SUBMITTED' && (
                        <div className={classes.icon}>
                            <div className={iconCls} />
                        </div>
                    )}
                    <div className={classes.text}>
                        {status === 'SUBMITTED' ? (
                            <h3 className={classes.h3}>Your Information</h3>
                        ) : (
                            <div>
                                <h3 className={classes.h3}>{hasAnsweredCoApp ? 'Final Review' : 'Review'}</h3>
                                <h5 className={classes.h5}>Tap the edit icon to modify the information</h5>
                            </div>
                        )}
                    </div>
                </section>
                <Form>
                    {!isCoApplicant && (
                        <LowIncomeWarning
                            contactPhone={formatPhone(contactPhone)}
                            isCCRMode={isCCRMode}
                            offer={offer}
                            onLowIncomeWarning={this.handleLowIncome} />
                    )}
                    {this.renderBuyerInfo()}
                    {!isDMSActive && this.renderCarInfo()}
                    {!isCashDeal && this.renderEmploymentInfo()}
                    {!isCashDeal && this.renderAdditionalInfo()}
                    {coAppUnlocked && this.renderBuyerInfo(true)}
                    {!isCashDeal && coAppUnlocked && this.renderEmploymentInfo(true)}
                    {!isCashDeal && coAppUnlocked && this.renderAdditionalInfo(true)}
                    {(!needsCoApp && !isNonResident && status !== 'SUBMITTED') && (
                        <div>
                            <Button btnStyle="alternate" onClick={() => NavigationService.next(coAppComp)}>Verify</Button>
                        </div>
                    )}
                    {needsCoApp && (
                        <div>
                            <Button btnStyle="alternate" onClick={() => NavigationService.next('addCoApp')}>Add Co-Applicant</Button>
                        </div>
                    )}
                </Form>
            </div>
        );
    }
}

const mapStateToProps = state => {
    const {config, creditor, dealInfo, navigation} = state;
    const {buyerHasCoApp, coAppUnlocked} = navigation;
    const {origin: offerOrigin, offerId, contactPhone} = config;
    const {theme, type} = creditor;
    const isCashDeal = DealInfoService.isCashDeal();
    const isDMSActive = get(state, 'creditor.isDMSActive', false);

    return {
        isCCRMode: SessionService.isCCRMode(),
        offerId,
        contactPhone,
        isCashDeal,
        isCoApplicant: NavigationService.isCoApplicant(),
        isCU: type === 'creditUnion',
        isDealer: type === 'dealer',
        isDMSActive,
        status: UIService.getStatus(),
        offerOrigin,
        requireCitizenshipProof: CreditorService.getRequireCitizenshipProof(),
        isPriorMemberAnswerRequired: CreditorService.getRequirePriorMembershipAnswer(),
        theme,
        offer: {
            navigation: {
                buyerHasCoApp,
                coAppUnlocked
            },
            dealInfo,
            vehicleInfo: VehicleInfoService.getReducerState(),
            ...EmploymentInfoService.getReducerState(),
            ...AdditionalIncomeService.getReducerState(),
            ...BuyerInfoService.getReducerState()
        }
    };
};

export default connect(mapStateToProps)(injectSheet(styles)(FinalReview));
